import React, { useState, ChangeEvent, useEffect } from 'react';
import {Card, Form, Input, Button, Select, Row, Col, Checkbox} from 'antd';
import dayjs from 'dayjs';

const { Option } = Select;

interface DateFunctionsProps {
    selectedFunction: string;
    onSave: (expression: string) => void;
}

const DateFunctions: React.FC<DateFunctionsProps> = ({ selectedFunction, onSave }) => {
    const [offsetDay, setOffsetDay] = useState<string>('');
    const [offsetHour, setOffsetHour] = useState<string>('');
    const [offsetMinute, setOffsetMinute] = useState<string>('');
    const [timeFormat, setTimeFormat] = useState<string>('yyyy-MM-dd');
    const [customFormat, setCustomFormat] = useState<string>('');
    const [useCustomFormat, setUseCustomFormat] = useState<boolean>(false);
    const [calculatedDate, setCalculatedDate] = useState<string>('');
    const [calculateTimeFromMidNight, setCalculateTimeFromMidNight] = useState<boolean>(false);

    useEffect(() => {
        calculateDate();
    }, [offsetDay, offsetHour, offsetMinute, timeFormat, customFormat, useCustomFormat]);

    const handleOffsetDayChange = (event: ChangeEvent<HTMLInputElement>) => {
        setOffsetDay(event.target.value);
    };

    const handleOffsetHourChange = (event: ChangeEvent<HTMLInputElement>) => {
        setOffsetHour(event.target.value);
    };

    const handleOffsetMinuteChange = (event: ChangeEvent<HTMLInputElement>) => {
        setOffsetMinute(event.target.value);
    };

    const handleTimeFormatChange = (value: string) => {
        if (value === 'custom') {
            setUseCustomFormat(true);
        } else {
            setUseCustomFormat(false);
            setTimeFormat(value);
        }
    };

    const handleCustomFormatChange = (event: ChangeEvent<HTMLInputElement>) => {
        setCustomFormat(event.target.value);
    };

    const handleSave = () => {
        let format = useCustomFormat ? customFormat : timeFormat;
        let expression = `${selectedFunction}()`;
        if (selectedFunction === 'dateTime') {
            expression = `${selectedFunction}(${offsetDay ? offsetDay : 0};${offsetHour ? offsetHour : 0};${offsetMinute ? offsetMinute : 0};${format};${calculateTimeFromMidNight})`;
        }
        onSave(expression);
    };

    const calculateDate = () => {
        let format = useCustomFormat ? customFormat : timeFormat;
        format = format.replace(/[dDyY]/g, function(match) {
            switch (match) {
                case 'd': return 'D';
                case 'D': return 'd';
                case 'y': return 'Y';
                case 'Y': return 'y';
                default: return match;  // Fallback für unerwartete Werte, sollte aber nie erreicht werden
            }
        });
        let date = dayjs().add(parseInt(offsetDay || '0'), 'day')
            .add(parseInt(offsetHour || '0'), 'hour')
            .add(parseInt(offsetMinute || '0'), 'minute');
        setCalculatedDate(date.format(format));
    };

    const resolveFunctionName = (selectedFunction: string) => {
        switch (selectedFunction) {
            case 'dateTime':
                return 'Datum-Zeit-Offset';
            default:
                return 'Funktion';
        }
    };

    const handleCheckboxChange = () => {
        setCalculateTimeFromMidNight(!calculateTimeFromMidNight);
    };

    const labelColSpan = 12;
    const wrapperColSpan = 12;

    return (
        <Card title={`Funktion: ${resolveFunctionName(selectedFunction)}`} bordered={false}>
            {selectedFunction === 'dateTime' && (
                <>
                    <Form.Item label="Offset (Tage)" labelCol={{ span: labelColSpan }} wrapperCol={{ span: wrapperColSpan }}>
                        <Input value={offsetDay} onChange={handleOffsetDayChange} />
                    </Form.Item>
                    <Form.Item label="Offset (Stunden)" labelCol={{ span: labelColSpan }} wrapperCol={{ span: wrapperColSpan }}>
                        <Input value={offsetHour} onChange={handleOffsetHourChange} />
                    </Form.Item>
                    <Form.Item label="Offset (Minuten)" labelCol={{ span: labelColSpan }} wrapperCol={{ span: wrapperColSpan }}>
                        <Input value={offsetMinute} onChange={handleOffsetMinuteChange} />
                    </Form.Item>
                    <Form.Item label="Datumsformat"labelCol={{ span: labelColSpan }} wrapperCol={{ span: wrapperColSpan }}>
                        <Select value={useCustomFormat ? 'custom' : timeFormat} onChange={handleTimeFormatChange} style={{ width: '100%' }}>
                            <Option value="yyyy-MM-dd">yyyy-MM-dd</Option>
                            <Option value="dd-MM-yyyy">dd-MM-yyyy</Option>
                            <Option value="MM-dd-yyyy">MM-dd-yyyy</Option>
                            <Option value="dd/MM/yyyy">dd/MM/yyyy</Option>
                            <Option value="yyyy-MM-dd HH:mm">yyyy-MM-dd HH:mm</Option>
                            <Option value="dd-MM-yyyy HH:mm">dd-MM-yyyy HH:mm</Option>
                            <Option value="MM-dd-yyyy HH:mm">MM-dd-yyyy HH:mm</Option>
                            <Option value="dd/MM/yyyy HH:mm">dd/MM/yyyy HH:mm</Option>
                            <Option value="custom">Benutzerdefiniert</Option>
                        </Select>
                    </Form.Item>
                    {useCustomFormat && (
                        <Form.Item label="Benutzerdefiniertes Format" labelCol={{ span: labelColSpan }} wrapperCol={{ span: wrapperColSpan }}>
                            <Input value={customFormat} onChange={handleCustomFormatChange} />
                        </Form.Item>
                    )}
                    <Form.Item style={{textAlign: 'center'}}>
                        <Checkbox name="timeCalculationIsMidNight" checked={calculateTimeFromMidNight} onChange={handleCheckboxChange}>Zeitberechnung ab 0 Uhr</Checkbox>
                    </Form.Item>
                </>
            )}
            <Form.Item>
                <Button type="primary" onClick={handleSave} style={{ width: '100%' }}>Speichern</Button>
            </Form.Item>
            {calculatedDate && (
                <Form.Item label="Berechnetes Datum" labelCol={{ span: labelColSpan }} wrapperCol={{ span: wrapperColSpan }}>
                    <Input value={calculatedDate} readOnly />
                </Form.Item>
            )}
            <div style={{ marginTop: '20px', fontSize: '12px' }}>
                <p><strong>Legende:</strong></p>
                <p><strong>yyyy:</strong> Jahr (4-stellig)</p>
                <p><strong>MM:</strong> Monat (2-stellig)</p>
                <p><strong>dd:</strong> Tag (2-stellig)</p>
                <p><strong>HH:</strong> Stunde (24-Stunden-Format)</p>
                <p><strong>mm:</strong> Minute (2-stellig)</p>
                <p><strong>ss:</strong> Sekunde (2-stellig)</p>
            </div>
        </Card>
    );
};

export default DateFunctions;
